import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { 
  Mail, 
  MailOpen, 
  Bell, 
  AlertCircle, 
  Info, 
  Megaphone,
  Clock,
  CheckCheck,
  ChevronDown,
  ChevronUp,
  X
} from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const API_URL = '';

// Message type icons and colors
const messageTypeConfig = {
  announcement: { icon: Megaphone, color: 'text-blue-400', bg: 'bg-blue-900/20', border: 'border-blue-500/30' },
  reminder: { icon: Bell, color: 'text-yellow-400', bg: 'bg-yellow-900/20', border: 'border-yellow-500/30' },
  alert: { icon: AlertCircle, color: 'text-red-400', bg: 'bg-red-900/20', border: 'border-red-500/30' },
  system: { icon: Info, color: 'text-purple-400', bg: 'bg-purple-900/20', border: 'border-purple-500/30' }
};

// Priority badges
const priorityConfig = {
  low: { label: 'Low', color: 'bg-gray-500' },
  normal: { label: '', color: '' },
  high: { label: 'Important', color: 'bg-orange-500' },
  urgent: { label: 'Urgent', color: 'bg-red-500' }
};

const MessageCenter = ({ onUnreadCountChange }) => {
  const { toast } = useToast();
  const [messages, setMessages] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [expandedMessage, setExpandedMessage] = useState(null);
  const [isCollapsed, setIsCollapsed] = useState(false);

  useEffect(() => {
    fetchMessages();
  }, []);

  useEffect(() => {
    if (onUnreadCountChange) {
      onUnreadCountChange(unreadCount);
    }
  }, [unreadCount, onUnreadCountChange]);

  const fetchMessages = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`${API_URL}/api/messages/inbox`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setMessages(response.data.messages || []);
      setUnreadCount(response.data.unread_count || 0);
    } catch (error) {
      console.error('Error fetching messages:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const markAsRead = async (messageId) => {
    try {
      const token = localStorage.getItem('access_token');
      await axios.post(`${API_URL}/api/messages/mark-read`, {
        message_ids: [messageId]
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Update local state
      setMessages(prev => prev.map(msg => 
        msg.message_id === messageId ? { ...msg, is_read: true } : msg
      ));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error marking message as read:', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      const token = localStorage.getItem('access_token');
      await axios.post(`${API_URL}/api/messages/mark-all-read`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setMessages(prev => prev.map(msg => ({ ...msg, is_read: true })));
      setUnreadCount(0);
      
      toast({
        title: "All messages marked as read",
        description: "Your inbox is now up to date."
      });
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  };

  const dismissMessage = async (messageId, e) => {
    e.stopPropagation(); // Prevent expanding the message
    
    try {
      const token = localStorage.getItem('access_token');
      await axios.delete(`${API_URL}/api/messages/dismiss/${messageId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Remove from local state
      const dismissedMsg = messages.find(m => m.message_id === messageId);
      setMessages(prev => prev.filter(msg => msg.message_id !== messageId));
      
      // Update unread count if the dismissed message was unread
      if (dismissedMsg && !dismissedMsg.is_read) {
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
      
      toast({
        title: "Message dismissed",
        description: "The message has been removed from your inbox."
      });
    } catch (error) {
      console.error('Error dismissing message:', error);
      toast({
        title: "Error",
        description: "Failed to dismiss message. Please try again.",
        variant: "destructive"
      });
    }
  };

  const toggleMessage = (messageId) => {
    if (expandedMessage === messageId) {
      setExpandedMessage(null);
    } else {
      setExpandedMessage(messageId);
      // Mark as read when expanded
      const msg = messages.find(m => m.message_id === messageId);
      if (msg && !msg.is_read) {
        markAsRead(messageId);
      }
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    
    // Less than 24 hours
    if (diff < 86400000) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }
    // Less than 7 days
    if (diff < 604800000) {
      return date.toLocaleDateString([], { weekday: 'short' });
    }
    // Otherwise show date
    return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
  };

  if (isLoading) {
    return (
      <Card className="p-4 bg-gray-900 border-gray-700">
        <div className="flex items-center justify-center py-4">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#FFCC00]"></div>
        </div>
      </Card>
    );
  }

  return (
    <Card className="bg-gray-900 border-gray-700 overflow-hidden">
      {/* Header */}
      <div 
        className="p-4 border-b border-gray-700 flex items-center justify-between cursor-pointer hover:bg-gray-800/50 transition-colors"
        onClick={() => setIsCollapsed(!isCollapsed)}
      >
        <div className="flex items-center">
          <Mail className="h-5 w-5 text-[#FFCC00] mr-2" />
          <h3 className="font-semibold text-white">Message Center</h3>
          {unreadCount > 0 && (
            <span className="ml-2 bg-red-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">
              {unreadCount}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {unreadCount > 0 && !isCollapsed && (
            <Button
              variant="ghost"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                markAllAsRead();
              }}
              className="text-xs text-gray-400 hover:text-white"
            >
              <CheckCheck className="h-3 w-3 mr-1" />
              Mark all read
            </Button>
          )}
          {isCollapsed ? (
            <ChevronDown className="h-5 w-5 text-gray-400" />
          ) : (
            <ChevronUp className="h-5 w-5 text-gray-400" />
          )}
        </div>
      </div>

      {/* Messages List */}
      {!isCollapsed && (
        <div className="max-h-[400px] overflow-y-auto">
          {messages.length === 0 ? (
            <div className="p-6 text-center text-gray-400">
              <MailOpen className="h-10 w-10 mx-auto mb-2 opacity-50" />
              <p>No messages yet</p>
              <p className="text-sm">Company announcements will appear here</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-800">
              {messages.map((message) => {
                const typeConfig = messageTypeConfig[message.message_type] || messageTypeConfig.system;
                const TypeIcon = typeConfig.icon;
                const priority = priorityConfig[message.priority];
                const isExpanded = expandedMessage === message.message_id;

                return (
                  <div
                    key={message.message_id}
                    className={`transition-colors ${!message.is_read ? 'bg-gray-800/50' : ''}`}
                  >
                    {/* Message Header */}
                    <div
                      className="p-4 cursor-pointer hover:bg-gray-800/30"
                      onClick={() => toggleMessage(message.message_id)}
                    >
                      <div className="flex items-start gap-3">
                        {/* Icon */}
                        <div className={`p-2 rounded-lg ${typeConfig.bg}`}>
                          <TypeIcon className={`h-4 w-4 ${typeConfig.color}`} />
                        </div>

                        {/* Content */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            {!message.is_read && (
                              <span className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0" />
                            )}
                            <h4 className={`font-medium truncate ${!message.is_read ? 'text-white' : 'text-gray-300'}`}>
                              {message.subject}
                            </h4>
                            {priority.label && (
                              <span className={`text-xs px-2 py-0.5 rounded ${priority.color} text-white`}>
                                {priority.label}
                              </span>
                            )}
                          </div>
                          
                          {!isExpanded && (
                            <p className="text-sm text-gray-400 truncate mt-1">
                              {message.content}
                            </p>
                          )}
                          
                          <div className="flex items-center gap-2 mt-1 text-xs text-gray-500">
                            <Clock className="h-3 w-3" />
                            <span>{formatDate(message.created_at)}</span>
                            <span>•</span>
                            <span>From: {message.sender_name}</span>
                          </div>
                        </div>

                        {/* Action Buttons */}
                        <div className="flex items-center gap-1 flex-shrink-0">
                          {/* Dismiss Button */}
                          <button
                            onClick={(e) => dismissMessage(message.message_id, e)}
                            className="p-1 rounded hover:bg-red-900/30 text-gray-500 hover:text-red-400 transition-colors"
                            title="Dismiss message"
                          >
                            <X className="h-4 w-4" />
                          </button>
                          
                          {/* Expand Icon */}
                          {isExpanded ? (
                            <ChevronUp className="h-5 w-5 text-gray-400" />
                          ) : (
                            <ChevronDown className="h-5 w-5 text-gray-400" />
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Expanded Content */}
                    {isExpanded && (
                      <div className={`px-4 pb-4 ml-11 mr-4 rounded-lg ${typeConfig.bg} ${typeConfig.border} border`}>
                        <div className="p-4">
                          <p className="text-gray-200 whitespace-pre-wrap">{message.content}</p>
                          <div className="mt-4 pt-3 border-t border-gray-700 flex justify-end">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e) => dismissMessage(message.message_id, e)}
                              className="text-red-400 hover:text-red-300 hover:bg-red-900/20"
                            >
                              <X className="h-4 w-4 mr-1" />
                              Dismiss
                            </Button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </Card>
  );
};

export default MessageCenter;
